'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { Billboard, type BillboardType } from '@pmndrs/vanilla';
import * as THREE from 'three';

import { actorParenting } from './actorParenting';
import { getClassIconPath } from './classHelpers';
import { reportEditorHookError } from './editorHookErrorReporter';
import { ICON_NAME } from './iconConstants';

import type { EditorObjectData } from '@/app/editor/core/editorClassData';

const averageWorldPositionScratch = new THREE.Vector3();


/** Recursively find ENGINE.Actor */
export function findActorFromTarget(target: ENGINE.Actor | ENGINE.SceneComponent | THREE.Object3D | null | undefined): ENGINE.Actor | null {
  if (!target) return null;
  if (target instanceof ENGINE.Actor) return target;
  if (target instanceof ENGINE.SceneComponent) return target.getActor();
  return findActorFromObject(target as THREE.Object3D);
}

/** Recursively find ENGINE.Actor from THREE Object3D */
export function findActorFromObject(object: THREE.Object3D): ENGINE.Actor | null {
  // Return if has actor property
  if ('actor' in object && object.actor instanceof ENGINE.Actor) return object.actor;
  // If object is a Scene, return undefined
  if (object.type === 'Scene') return null;
  // Recursive case: Check parent if it exists
  if (object.parent) return findActorFromObject(object.parent);
  // If no parent and no actor, return undefined
  return null;
}

/** Detaches all children from group and adds them back to scene */
export function detachGroupChildren(selectionGroup: THREE.Group, scene: THREE.Scene) {
  while (selectionGroup.children.length) {
    const child = selectionGroup.children[0];
    const worldPosition = child.getWorldPosition();
    const worldQuaternion = child.getWorldQuaternion();
    const worldScale = child.getWorldScale();
    selectionGroup.remove(child);

    // Parented actors should return to their actor parent, not always to scene root.
    const actor = child instanceof ENGINE.SceneComponent ? child.getActor() : null;
    const parentActor = actor ? actorParenting.getParentActor(actor) : null;
    if (parentActor?.rootComponent) {
      try {
        parentActor.rootComponent.attach(child);
        continue;
      } catch (error) {
        console.warn(`[objectHelpers] Failed to reattach selection child to parent actor ${parentActor.uuid}`, error);
      }
    }

    scene.add(child);
    child.position.copy(worldPosition);
    child.quaternion.copy(worldQuaternion);
    child.scale.copy(worldScale);
  }
}

/** Attaches objects to selection group and updates its position based on average world position */
export function attachToGroup(selectionGroup: THREE.Group, actors: ENGINE.Actor[]) {
  const averagePosition = calculateAverageWorldPosition(actors);
  if (averagePosition) selectionGroup.position.copy(averagePosition);
  actors.forEach(actor => selectionGroup.attach(actor.rootComponent));
}

export function getAttachedGroup(object: THREE.Object3D | ENGINE.Actor): THREE.Group | undefined {
  if (object instanceof THREE.Object3D) {
    return object.parent instanceof THREE.Group ? object.parent as THREE.Group : undefined;
  }
  if (object instanceof ENGINE.Actor) {
    return getAttachedGroup(object.rootComponent);
  }
  return undefined;
}

export function calculateAverageWorldPosition(targets: (THREE.Object3D | ENGINE.Actor)[]): THREE.Vector3 | null {
  if (!targets?.length) return null;
  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;
  let positionCount = 0;

  for (let index = 0; index < targets.length; index += 1) {
    const target = targets[index];
    const sceneObject = target instanceof ENGINE.Actor ? target.rootComponent : target;
    if (!sceneObject) continue;

    sceneObject.getWorldPosition(averageWorldPositionScratch);
    sumX += averageWorldPositionScratch.x;
    sumY += averageWorldPositionScratch.y;
    sumZ += averageWorldPositionScratch.z;
    positionCount += 1;
  }

  if (positionCount === 0) return null;
  const inverseCount = 1 / positionCount;
  return new THREE.Vector3(sumX * inverseCount, sumY * inverseCount, sumZ * inverseCount);
}

/** Returns the world rotation of an object or actor as Euler angles in degrees */
export function getWorldRotationDegrees(target: THREE.Object3D | ENGINE.Actor): THREE.Vector3 {
  target = target instanceof ENGINE.Actor ? target.rootComponent : target;
  const group = target instanceof THREE.Group ? target.parent : null;
  if (group) {
    const rotation = group.getWorldRotation().toArray();
    return new THREE.Vector3(THREE.MathUtils.radToDeg(rotation[0]), THREE.MathUtils.radToDeg(rotation[1]), THREE.MathUtils.radToDeg(rotation[2]));
  }
  const rotation = target.rotation.toArray();
  return new THREE.Vector3(THREE.MathUtils.radToDeg(rotation[0]), THREE.MathUtils.radToDeg(rotation[1]), THREE.MathUtils.radToDeg(rotation[2]));
}

export function getRotationDegrees(target: THREE.Object3D): THREE.Vector3 {
  const rotation = target.rotation.toArray();
  return new THREE.Vector3(THREE.MathUtils.radToDeg(rotation[0]), THREE.MathUtils.radToDeg(rotation[1]), THREE.MathUtils.radToDeg(rotation[2]));
}

export function getPrefabAssetName(target: ENGINE.Actor | null | undefined): string | undefined {
  return (target?.editorData as EditorObjectData)?.prefabAsset;
}

export function getPrefabName(target: ENGINE.Actor | null | undefined): string | undefined {
  const prefabAssetName = getPrefabAssetName(target);
  return prefabAssetName ?? target?.getPrefabName() ?? undefined;
}

/** Gets the display label for a prefab by extracting the filename without extension from the prefab path */
export function getPrefabLabel(target: ENGINE.Actor | null | undefined): string | undefined {
  const prefabName = getPrefabName(target);
  return prefabName ? prefabName.split('/').pop()?.replace(/\..+$/, '') : undefined;
}

/** Checks if the target actor is a prefab asset (non-world scene) */
export function isPrefabAsset(target: ENGINE.Actor | null | undefined): boolean {
  return !!getPrefabAssetName(target);
}

/** Checks if the target actor is a prefab instance (in the world scene) */
export function isPrefabInstance(target: ENGINE.Actor | null | undefined): boolean {
  return !!getPrefabName(target) && !isPrefabAsset(target);
}

export function isSelectable(target: THREE.Object3D | ENGINE.Actor): boolean {
  if (target instanceof ENGINE.Actor) {
    const editorData = target.editorData as EditorObjectData | undefined;
    return !target.isHidden() && !editorData?.hidden && !target.isTransient() && editorData?.selectable !== false;
  }
  const actor = findActorFromObject(target);
  return actor ? isSelectable(actor) : false; // TODO: <AY> add check for selectable components
}

export function hideActorInEditor(actor: ENGINE.Actor, hide: boolean) {
  (actor.editorData as EditorObjectData).hidden = hide;
  if (hide) {
    actor.rootComponent.traverse(obj => obj.layers.disableAll());
    try {
      actor.onEditorRemoveFromWorld();
    } catch (error) {
      reportEditorHookError({
        hook: 'onEditorRemoveFromWorld',
        context: 'objectHelpers.hideActorInEditor',
        target: actor,
        error,
      });
    }
  } else {
    try {
      actor.onEditorAddToWorld();
    } catch (error) {
      reportEditorHookError({
        hook: 'onEditorAddToWorld',
        context: 'objectHelpers.hideActorInEditor',
        target: actor,
        error,
      });
    }
    actor.rootComponent.traverse(obj => obj.layers.enable(0));
  }
}

export function lockActorInEditor(actor: ENGINE.Actor, lock: boolean) {
  (actor.editorData as EditorObjectData).selectable = !lock;
}

export function getDirectChildSceneComponents(target: ENGINE.Actor | ENGINE.SceneComponent): ENGINE.SceneComponent[] {
  const comp = (target instanceof ENGINE.Actor) ? target.rootComponent : target;
  return comp.children.filter(child => child instanceof ENGINE.SceneComponent) as ENGINE.SceneComponent[];
}

export function findMatchingSceneComponentFromActors(compToMatch: ENGINE.SceneComponent, actors: ENGINE.Actor[]) {
  const index = actors[0].getComponents(ENGINE.SceneComponent).findIndex(comp => comp.uuid === compToMatch.uuid);
  if (index === -1) return [];
  const sceneCompsPerTarget = actors.map(actor => actor.getComponents(ENGINE.SceneComponent));
  return sceneCompsPerTarget.map(comps => comps[index]);
}

export function findMatchingSubObjectFromActor(objectToMatch: ENGINE.SceneComponent, actor: ENGINE.Actor) {
  if (objectToMatch instanceof ENGINE.SceneComponent) {
    const index = objectToMatch.getActor()?.getComponents(ENGINE.SceneComponent).findIndex(comp => comp.uuid === objectToMatch.uuid) ?? -1;
    if (index > -1) {
      const mySceneComp = actor.getComponents(ENGINE.SceneComponent)[index];
      return (mySceneComp?.constructor === objectToMatch.constructor) ? mySceneComp : null;
    }
  }
  return null;
}

export function getClassName(object: any, shortName: boolean = false): string {
  const className = ENGINE.ClassRegistry.getClassName(object);
  return className ? (shortName ? className.replace(/^(ENGINE\.|GAME\.|THREE\.)/, '') : className) : 'Unknown Class';
}

const ExcludedBillboardIconNames = [
  ICON_NAME.MeshComponent,
  ICON_NAME.GLFTMeshComponent,
];

/** Cache entry for shared SpriteMaterial */
interface ObjectIconMaterialCacheEntry {
  material: THREE.SpriteMaterial;
  refcount: number;
}

/** Module-level cache for object icon SpriteMaterials keyed by icon path */
const objectIconMaterialCache = new Map<string, ObjectIconMaterialCacheEntry>();
const objectIconSpritePool: THREE.Sprite[] = [];
const pooledObjectIconSpriteMaterial = new THREE.SpriteMaterial({
  visible: false,
  depthTest: false,
  depthWrite: false,
  transparent: true,
  opacity: 0,
});

/**
 * Acquires a shared SpriteMaterial for an object icon.
 * Creates the material if it doesn't exist, otherwise returns cached material and increments refcount.
 * @param iconPath - The path to the icon texture (used as cache key).
 * @param texture - The loaded texture to use for the material.
 * @returns The shared SpriteMaterial.
 */
export function acquireObjectIconMaterial(iconPath: string, texture: THREE.Texture): THREE.SpriteMaterial {
  const existing = objectIconMaterialCache.get(iconPath);
  if (existing) {
    existing.refcount++;
    return existing.material;
  }

  const material = new THREE.SpriteMaterial({
    map: texture,
    depthTest: true
  });

  objectIconMaterialCache.set(iconPath, { material, refcount: 1 });
  return material;
}

/**
 * Releases a shared SpriteMaterial for an object icon.
 * Decrements refcount and keeps the material cached until world teardown.
 * Does NOT dispose the texture as it is managed by ENGINE.resourceManager.
 * @param iconPath - The path to the icon texture (used as cache key).
 */
export function releaseObjectIconMaterial(iconPath: string): void {
  const entry = objectIconMaterialCache.get(iconPath);
  if (!entry) {
    console.warn(`[objectHelpers] Attempted to release unknown object icon material: ${iconPath}`);
    return;
  }

  entry.refcount = Math.max(0, entry.refcount - 1);
}

/**
 * Acquires an icon sprite from a pool and assigns the given material.
 */
export function acquireObjectIconSprite(material: THREE.SpriteMaterial): THREE.Sprite {
  const sprite = objectIconSpritePool.pop() ?? new THREE.Sprite();
  sprite.material = material;
  sprite.visible = true;
  sprite.position.set(0, 0, 0);
  sprite.quaternion.identity();
  sprite.scale.set(1, 1, 1);
  sprite.setTransient(true);
  return sprite;
}

/**
 * Returns an icon sprite to the pool without disposing geometry.
 */
export function releaseObjectIconSprite(sprite: THREE.Sprite): void {
  sprite.removeFromParent();
  sprite.material = pooledObjectIconSpriteMaterial;
  objectIconSpritePool.push(sprite);
}

/**
 * Evicts object icon materials after ResourceManager cache invalidation.
 * Materials are disposed only on this world-level boundary, not per actor remove.
 */
export function evictObjectIconMaterialsAfterResourceClear(): void {
  objectIconMaterialCache.forEach(entry => {
    try {
      entry.material.dispose();
    } catch {
      // Three.js WebGPU NodeManager can throw if the renderer was already torn down.
    }
  });
  objectIconMaterialCache.clear();
}

/** Extended BillboardType that tracks the icon path for material release */
export interface ObjectIconBillboard extends BillboardType {
  /** The icon path used for the material cache key */
  iconPath?: string;
}

/**
 * Add an object icon billboard to an actor.
 * @param actor - The actor to add the object icon to.
 * @returns The billboard object if successful, otherwise undefined.
 */
export async function createActorBillboardIcon(actor: ENGINE.Actor): Promise<ObjectIconBillboard | undefined> {
  const iconPath = await getClassIconPath(actor, ExcludedBillboardIconNames);
  if (!iconPath) return undefined;

  const billboard: ObjectIconBillboard = Billboard();
  billboard.group.setTransient(true);
  billboard.iconPath = iconPath;

  // Load texture using ENGINE's ResourceManager (already cached by ResourceManager)
  const texture = await ENGINE.resourceManager.loadTexture(ENGINE.AssetPath.fromString(iconPath));
  if (!texture) {
    console.warn(`🚫 Failed to load object icon texture: ${iconPath}`);
    return undefined;
  }

  // Acquire shared material from cache
  const material = acquireObjectIconMaterial(iconPath, texture);

  const sprite = acquireObjectIconSprite(material);
  billboard.group.add(sprite);
  return billboard;
}

/**
 * Cleans up an object icon billboard without disposing WebGPU resources.
 * @param billboard - The billboard to clean up.
 */
export function cleanupObjectIconBillboard(billboard: ObjectIconBillboard): void {
  billboard.group.removeFromParent();
  billboard.group.getComponents(THREE.Sprite).forEach(sprite => {
    releaseObjectIconSprite(sprite);
  });
  billboard.group.clear();

  if (billboard.iconPath) {
    releaseObjectIconMaterial(billboard.iconPath);
  }
}

/**
 * Gets the hierarchical path of a scene component within its actor (e.g., "0.1.0" for nested components)
 * @param component - The scene component to get the path of.
 * @returns The hierarchical path of the component or null if the component is not part of the actor's hierarchy.
 */
export function getComponentPath(component: ENGINE.SceneComponent): string | null {
  const actor = component.getActor();
  if (!actor) return null;

  const rootComponent = actor.rootComponent;
  if (component === rootComponent) return '0';

  // Build path by traversing up the hierarchy
  const pathSegments: number[] = [];
  let currentComponent: ENGINE.SceneComponent | null = component;

  while (currentComponent && currentComponent !== rootComponent) {
    const parent: THREE.Object3D | null = currentComponent.parent;
    if (!parent || !(parent instanceof ENGINE.SceneComponent)) break;

    // Find the index of current component among its parent's scene component children
    const siblings = getDirectChildSceneComponents(parent);
    const index = siblings.indexOf(currentComponent);
    if (index === -1) return null; // Component not found in parent's children

    pathSegments.unshift(index);
    currentComponent = parent;
  }

  // If we didn't reach the root component, the component is not part of this actor's hierarchy
  if (currentComponent !== rootComponent) return null;

  return '0.' + pathSegments.join('.');
}

/**
 * Finds a scene component within an actor using its hierarchical path (e.g., "0.1.0")
 * @param actor - The actor to find the component in.
 * @param path - The hierarchical path of the component to find.
 * @returns The scene component or null if the component is not found.
 */
export function getComponentFromPath(actor: ENGINE.Actor, path: string): ENGINE.SceneComponent | null {
  if (!path) return null;

  const pathSegments = path.split('.');
  if (pathSegments[0] !== '0') return null; // Path must start with root component

  let currentComponent: ENGINE.SceneComponent = actor.rootComponent;

  // Traverse the path segments (skip the first '0' which is the root)
  for (let i = 1; i < pathSegments.length; i++) {
    const index = parseInt(pathSegments[i], 10);
    if (isNaN(index)) return null;

    const childComponents = getDirectChildSceneComponents(currentComponent);
    if (index >= childComponents.length) return null;

    currentComponent = childComponents[index];
  }

  return currentComponent;
}

